import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

/** Insertion position after removing the dragged effect, including expanded cards. */
export function effectDropIndex(rows: readonly { top: number; height: number }[], from: number, y: number): number {
  return rows.reduce((to, row, index) => to + (index !== from && y > row.top + row.height / 2 ? 1 : 0), 0);
}

type Row = { top: number; height: number };

/** Keep DOM order (and pointer capture) intact while opening the destination gap. */
export function effectPreviewOffsets(rows: readonly Row[], from: number, to: number | null, gap: number): number[] {
  if (to === null || to === from) return rows.map(() => 0);
  const source = rows[from]!;
  const distance = source.height + gap;
  return rows.map((row, index) => {
    if (index === from) return to < from ? rows[to]!.top - row.top : rows[to]!.top + rows[to]!.height - source.height - row.top;
    if (from < index && index <= to) return -distance;
    if (to <= index && index < from) return distance;
    return 0;
  });
}

type Drag = { from: number; to: number | null; left: number; top: number; width: number; offsets: number[] };
type Held = {
  from: number; to: number | null; pointer: number; target: HTMLButtonElement;
  x: number; y: number; startX: number; startY: number; active: boolean;
  signature: string; scroll: HTMLElement | null; rows: Row[]; gap: number;
  source: { left: number; top: number; width: number };
};

/** Preview while dragging, then commit one undoable edit on release. */
export function useEffectReorder(stack: RefObject<HTMLDivElement | null>, signature: string, move: (from: number, to: number) => void) {
  const latest = useRef({ signature, move });
  latest.current = { signature, move };
  const held = useRef<Held | null>(null);
  const frame = useRef<number | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const update = () => {
    const current = held.current, list = stack.current;
    if (!current?.active || !list) return;
    const bounds = list.getBoundingClientRect();
    const viewport = current.scroll?.getBoundingClientRect();
    const top = Math.max(0, viewport?.top ?? bounds.top) - 28;
    const bottom = Math.min(window.innerHeight, viewport?.bottom ?? bounds.bottom) + 28;
    current.to = current.x < bounds.left - 20 || current.x > bounds.right + 20 || current.y < top || current.y > bottom ? null
      : effectDropIndex(current.rows, current.from, current.y - bounds.top);
    // Measure against the original layout, not the animated rows, to avoid jitter.
    setDrag({
      from: current.from, to: current.to,
      left: Math.max(8, Math.min(window.innerWidth - current.source.width - 8, current.source.left + current.x - current.startX - 16)),
      top: current.source.top + current.y - current.startY - 8,
      width: current.source.width,
      offsets: effectPreviewOffsets(current.rows, current.from, current.to, current.gap),
    });
  };
  const stop = (commit = false) => {
    const current = held.current;
    held.current = null;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    setDrag(null);
    if (!current) return;
    if (current.target.hasPointerCapture(current.pointer)) current.target.releasePointerCapture(current.pointer);
    // A remote edit, selection change, or cancelled gesture must not reorder stale indices.
    if (commit && current.active && current.to !== null && current.to !== current.from && current.signature === latest.current.signature && current.target.isConnected && !current.target.matches(':disabled')) {
      latest.current.move(current.from, current.to);
    }
  };
  const stopRef = useRef(stop);
  stopRef.current = stop;
  useEffect(() => {
    if (held.current && held.current.signature !== signature) stopRef.current();
  }, [signature]);
  useEffect(() => {
    const cancel = () => stopRef.current();
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && held.current) { event.preventDefault(); event.stopPropagation(); cancel(); }
    };
    window.addEventListener('keydown', key, true);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('blur', cancel);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      held.current = null;
    };
  }, []);
  const scroll = () => {
    const current = held.current;
    if (!current?.active) return;
    if (current.scroll) {
      const bounds = current.scroll.getBoundingClientRect();
      if (current.x >= bounds.left && current.x <= bounds.right) {
        const top = Math.max(0, bounds.top), bottom = Math.min(window.innerHeight, bounds.bottom);
        const delta = current.y < top + 28 ? -8 : current.y > bottom - 28 ? 8 : 0;
        if (delta) { current.scroll.scrollTop += delta; update(); }
      }
    }
    frame.current = requestAnimationFrame(scroll);
  };
  return {
    drag,
    handlers: (from: number) => ({
      onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0 || held.current || event.currentTarget.matches(':disabled')) return;
        const list = stack.current, source = list?.children[from];
        if (!list || !source) return;
        const bounds = list.getBoundingClientRect();
        const rows = [...list.children].map(row => {
          const rect = row.getBoundingClientRect();
          return { top: rect.top - bounds.top, height: rect.height };
        });
        event.preventDefault();
        event.currentTarget.focus({ preventScroll: true });
        let parent = stack.current?.parentElement ?? null;
        while (parent && !/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) parent = parent.parentElement;
        held.current = { from, to: from, pointer: event.pointerId, target: event.currentTarget,
          startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY,
          active: false, signature: latest.current.signature, scroll: parent, rows,
          gap: parseFloat(getComputedStyle(list).rowGap) || 0, source: source.getBoundingClientRect() };
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>) => {
        const current = held.current;
        if (!current || current.pointer !== event.pointerId) return;
        current.x = event.clientX; current.y = event.clientY;
        if (!current.active) {
          if (Math.hypot(current.x - current.startX, current.y - current.startY) < 4) return;
          current.active = true;
          frame.current = requestAnimationFrame(scroll);
        }
        update();
      },
      onPointerUp: (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (held.current?.pointer !== event.pointerId) return;
        held.current.x = event.clientX; held.current.y = event.clientY;
        update(); stop(true);
      },
      onPointerCancel: () => stop(),
      onLostPointerCapture: () => { if (held.current) stop(); },
    }),
  };
}
